import { useAtom, useSetAtom } from "jotai";
import React, { useLayoutEffect } from "react";
import { Platform } from "react-native";

import { SentryError, SignOutButton } from "../../../ErrorHandling";
import { PlatformSpecificErrorBoundary } from "../../../ErrorHandling/components/PlatformSpecificErrorBoundary";
import { isUserAuthenticated_atom } from "../../atoms";
import { setPreAuthenticatedPathAtom } from "../../atoms/actions/setPreAuthenticatedPath/setPreAuthenticatedPath";
import {
  getWebPreAuthenticationPath,
  isValidPreAuthenticationPath,
} from "../../utils/getWebPreAuthenticationPath";

/**
 *
 * Handles catching any errors from children and checking for an unauthenticated error.
 * If we find one, we log the user out and clear our tokens (which will end up sending the user to
 * the initial screen.)
 *
 * If we find any other error, we just throw it again for another error boundary to find.
 */
export const AuthenticationErrorBoundary = ({
  children,
}: {
  children: React.JSX.Element;
}) => {
  return (
    <PlatformSpecificErrorBoundary
      fallback={(eventId, error, resetError) => (
        <ErrorFallback
          error={error}
          resetError={resetError}
          eventId={eventId}
        />
      )}
    >
      {children}
      <PreAuthenticatedPathHandler />
    </PlatformSpecificErrorBoundary>
  );
};

const PreAuthenticatedPathHandler = () => {
  const [isAuthenticated] = useAtom(isUserAuthenticated_atom);

  const setPreAuthenticatedPath = useSetAtom(setPreAuthenticatedPathAtom);

  useLayoutEffect(() => {
    if (Platform.OS === "web" && !isAuthenticated) {
      const path = getWebPreAuthenticationPath();
      if (isValidPreAuthenticationPath(path)) {
        setPreAuthenticatedPath({ path });
      }
    }
  }, [setPreAuthenticatedPath, isAuthenticated]);
  return null;
};

function ErrorFallback({
  error,
  resetError,
  eventId,
}: {
  error: any;
  resetError: any;
  eventId: string;
}) {
  return (
    <SentryError
      resetError={resetError}
      signOutButton={<SignOutButton {...{ resetError }} />}
      eventId={eventId}
    />
  );
}
